import { getColourArray, offline, uuidv4 } from "../../../utils/commonFunctions";
import { AreaOfInterest, GeoFeatureDataType, GroundStationDataType, GroupOrbitDataType } from "../types"
import * as Cesium from "cesium";

const getAllEntitiesFromDataSources = (viewer: Cesium.Viewer | null): Cesium.Entity[] => {
    if (!viewer) return [];
    try {
        const allEntities: Cesium.Entity[] = [];
        const dataSources = viewer.dataSources;

        for (let i = 0; i < dataSources.length; i++) {
            const dataSource = dataSources.get(i);
            if (dataSource && dataSource.entities) {
                allEntities.push(...dataSource.entities.values);
            }
        }
        return allEntities;
    } catch (error) {
        return [];
    }
};

export const toggleHideShowEntities = (viewer: Cesium.Viewer | null, entityId: string[], type: string) => {
    if (!viewer || !entityId) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        if (entity && entityId.includes(entity.id)) {
            entity.show = type === 'show';
        }
    }
    requestRenderViewer(viewer);
}

const hideEntity = (viewer: Cesium.Viewer | null, entityId: string[]) => {
    if (!viewer || !entityId) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        if (entity && entityId.includes(entity.id)) {
            entity.show = false;
        }
    }
    requestRenderViewer(viewer);
}
const showEntity = (viewer: Cesium.Viewer | null, entityId: string[], hiddenEntities: string[]) => {
    if (!viewer || !entityId) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        if (entity && entityId.includes(entity.id) && !hiddenEntities.includes(entity.id)) {
            entity.show = true;
        }
    }
    requestRenderViewer(viewer);
}

const getEntityById = (viewer: Cesium.Viewer | null, dataSourceName: string, entityId: string) => {
    if (!viewer) return
    const dataSource = getDataSourceByName(viewer, dataSourceName)
    if (!dataSource) return
    const entity = dataSource.entities.getById(entityId);
    return entity
}

const requestRenderViewer = (viewer: Cesium.Viewer | null) => {
    if (!viewer) return
    viewer.scene.requestRender();
    setTimeout(() => {
        viewer.scene.requestRender();
    }, 100);
}


const removeDataSourceByName = (viewer: Cesium.Viewer | null, dataSourceName: string) => {
    if (!viewer) return
    const dataSources = viewer.dataSources;
    if (!dataSources) return
    for (let i = 0; i < dataSources.length; i++) {
        const dataSource = dataSources.get(i);
        if (dataSource.name === dataSourceName) {
            dataSources.remove(dataSource);
            requestRenderViewer(viewer);
            return
        }
    }
    requestRenderViewer(viewer);
}

const getDataSourceByName = (viewer: Cesium.Viewer | null, dataSourceName: string) => {
    if (!viewer) return
    const dataSources = viewer.dataSources;
    if (!dataSources) return
    for (let i = 0; i < dataSources.length; i++) {
        const dataSource = dataSources.get(i);
        if (dataSource.name === dataSourceName) {
            return dataSource;
        }
    }
    return null;
}

const removeEntityById = (viewer: Cesium.Viewer | null, dataSourceName: string, entityId: string) => {
    if (!viewer) return
    const dataSource = getDataSourceByName(viewer, dataSourceName)
    if (!dataSource) return
    const entity = dataSource.entities.getById(entityId);
    if (!entity) return
    dataSource.entities.remove(entity)
    requestRenderViewer(viewer)
}

const getMaxEndTime = (orbits: any[]) => {
    return Math.max(...orbits.map(orbit => (orbit.startTime * 1000) + (orbit.duration * 60000)));
};
const getMinStartTime = (orbits: any[]) => {
    return Math.min(...orbits.map(orbit => orbit.startTime * 1000));
};

const getArrayCoordinates = (nestedArray: any[]) => {
    let resultArray = [];
    resultArray = resultArray.concat(...nestedArray);
    let finalArray: any[] = [];
    for (let i = 0; i < resultArray.length; i++) {
        finalArray.push(resultArray[i]);  // Push the current element
        if ((i % 2 === 1)) { // i >= 2 ensures we start from the 3rd element
            finalArray.push(0);  // Push 0 after every element at the specified odd index
        }
    }
    return finalArray;
}


const createOrbitsPackets = (viewer: Cesium.Viewer | null, groups: GroupOrbitDataType[], hiddenOrbits: string[], altitude: any) => {
    if (!viewer) return
    try {
        const orbits = groups.flatMap(group => group?.orbit_report || [])
        const minStartTime = new Date(getMinStartTime(orbits)).toISOString().split('.')?.[0] + 'Z';
        const maxEndTime = new Date(getMaxEndTime(orbits)).toISOString().split('.')?.[0] + 'Z';
        const availability = `${minStartTime}/${maxEndTime}`;
        groups.forEach(group => {
            const groupId = group.group_id
            const packets: any = []
            const show = hiddenOrbits.includes(groupId) ? false : true
            group.orbit_report.map(orbit => {
                const colorString = orbit.orbitColor
                const colorArray = getColourArray(colorString)
                packets.push({
                    availability: availability,
                    show: show,
                    billboard: {
                        eyeOffset: { cartesian: [0, 0, 0] },
                        horizontalOrigin: "CENTER",
                        image: '/assets/satellite_inactive.svg',
                        pixelOffset: { cartesian2: [0, 0] },
                        scale: 1,
                        show: true,
                        verticalOrigin: "CENTER"
                    },
                    parent: groupId,
                    description: "orbit",
                    id: orbit?.satelliteId,
                    name: orbit.name,
                    path: {
                        leadTime: [{ number: altitude[group?.group_id] > 2500 ? 21000 : 5000 }],
                        width: 1,
                        material: {
                            solidColor: {
                                color: {
                                    rgba: [colorArray[0], colorArray[1], colorArray[2], 255]
                                }
                            }
                        },
                        resolution: 10,
                        show: true,
                        trailTime: [{ number: 300 }]
                    },
                    position: {
                        cartographicDegrees: orbit.orbit_points, // Ensure this is in [lon, lat, height] format
                        epoch: availability.split('/')[0]
                    },
                    label: {
                        fillColor: Cesium.Color.fromBytes(255, 255, 255, 255),
                        font: 'montserrat',
                        horizontalOrigin: 'LEFT',
                        outlineColor: Cesium.Color.fromBytes(0, 0, 0, 255),
                        outlineWidth: 2,
                        pixelOffset: {
                            cartesian2: [
                                10,
                                0
                            ]
                        },
                        show: false,
                        style: 'FILL_AND_OUTLINE',
                        verticalOrigin: 'CENTER',
                        text: orbit.name,
                    },
                })
            })
            const dataSource = new Cesium.CzmlDataSource();
            removeDataSourceByName(viewer, groupId);
            viewer.dataSources.add(dataSource.load([
                {
                    clock: {
                        interval: availability,
                        currentTime: availability.split('/')[0],
                        multiplier: 0,
                        range: "CLAMPED",
                        step: "SYSTEM_CLOCK_MULTIPLIER"
                    },
                    id: "document",
                    name: groupId,
                    version: "1.0"
                },
                ...packets
            ])).then(loadedDataSource => {
                const entity = loadedDataSource.entities.getById(groupId);
                if (entity) {
                    entity.show = show;
                }
            });
        })

        requestRenderViewer(viewer);
    } catch (error) {
        console.log(error);
    }

}

const createAllGeoFeaturePacket = (viewer: Cesium.Viewer | null, aois: GeoFeatureDataType[], hiddenFeatures: string[]) => {
    if (!viewer) return
    try {
        const polygons: any = []
        const targets: any = []
        aois?.forEach((aoi) => {
            const name = aoi.name
            const uniqueId = aoi.areaOfInterestId
            const show = hiddenFeatures.includes(uniqueId) ? false : true
            if (aoi?.type === 'Geofence') {
                const coordinates = getArrayCoordinates(aoi.polygon);
                polygons.push(
                    {
                        "description": "aoi",
                        "id": uniqueId,
                        "name": name,
                        "show": show,
                        "polygon": {
                            "extrudedHeight": 0,
                            "height": 0,
                            "material": {
                                "solidColor": {
                                    "color": {
                                        "rgba": [
                                            9, 9, 9, 50
                                        ]
                                    }
                                }
                            },
                            "outline": true,
                            "outlineColor": {
                                "rgba": [240, 240, 240, 80]
                            },
                            "arcType": "RHUMB",
                            "positions": {
                                "cartographicDegrees": coordinates
                            },
                        }
                    }
                )
            } else if (aoi?.type === "Target Track") {
                targets.push({
                    "billboard": {
                        "horizontalOrigin": Cesium.HorizontalOrigin.CENTER,  // Use Cesium constants
                        "image": '/assets/gs_target.svg',
                        "pixelOffset": new Cesium.Cartesian2(0, 0),          // Correctly use Cartesian2
                        "show": true,
                        "verticalOrigin": Cesium.VerticalOrigin.CENTER
                    },
                    "description": 'target',
                    "id": uniqueId,
                    "name": name,
                    "show": show,
                    "label": {
                        "fillColor": Cesium.Color.fromBytes(255, 255, 255, 255), // Correct RGBA to Cesium.Color
                        "font": "montserrat",
                        "horizontalOrigin": "LEFT",        // Use Cesium constants
                        "outlineColor": Cesium.Color.fromBytes(0, 0, 0, 255),    // Correct outline RGBA
                        "outlineWidth": 2,
                        "pixelOffset": {
                            "cartesian2": [
                                15,
                                0
                            ]
                        },
                        "show": false,
                        "style": "FILL_AND_OUTLINE",             // Use Cesium constants
                        "verticalOrigin": "CENTER",          // Use Cesium constants
                        "text": name,
                    },
                    "position": {
                        "cartographicDegrees":
                            [
                                aoi?.coordinates?.long,
                                aoi?.coordinates?.lat,
                                0,
                            ]
                    }
                })
            }
        })
        const aoi_dataSource = new Cesium.CzmlDataSource();
        const target_dataSource = new Cesium.CzmlDataSource();
        removeDataSourceByName(viewer, 'AOI_Object')
        removeDataSourceByName(viewer, 'TARGET_Object')

        viewer.dataSources.add(aoi_dataSource.load(
            [
                {
                    "id": "document",
                    "name": "AOI_Object",
                    "version": "1.0"
                },
                ...polygons,
            ]
        ))
        viewer.dataSources.add(target_dataSource.load(
            [
                {
                    "id": "document",
                    "name": "TARGET_Object",
                    "version": "1.0"
                },
                ...targets
            ]
        ))
        requestRenderViewer(viewer);
    } catch (error) {
        console.log(error);
    }
}

const createAllGroundStationsPacket = (viewer: Cesium.Viewer | null, stations: GroundStationDataType[], hiddenStations: string[]) => {
    if (!viewer) return
    try {
        const packet: any = []
        stations?.forEach((station) => {
            const name = station.groundStationName
            const uniqueId = station.groundStationId
            const latitude = station.properties.latitude || 0
            const longitude = station.properties.longitude || 0
            const show = !hiddenStations.includes(uniqueId)
            packet.push({
                "billboard": {
                    "horizontalOrigin": Cesium.HorizontalOrigin.CENTER,
                    "verticalOrigin": Cesium.VerticalOrigin.CENTER,
                    "image": '/assets/un_select_gs.svg',
                    "scale": 0.8,
                    "show": true
                },
                "description": "station",
                "label": {
                    "fillColor": Cesium.Color.fromBytes(255, 255, 255, 255),
                    "font": "montserrat",
                    "horizontalOrigin": "LEFT",
                    "outlineColor": Cesium.Color.fromBytes(0, 0, 0, 255),
                    "outlineWidth": 2,
                    "pixelOffset": {
                        "cartesian2": [
                            15,
                            0
                        ]
                    },
                    "show": false,
                    "style": "FILL_AND_OUTLINE",
                    "verticalOrigin": "CENTER",
                    "text": name,
                },
                "name": name,
                "position": {
                    "cartographicDegrees": [
                        longitude,
                        latitude,
                        0,
                    ]
                },
                "id": uniqueId,
                "show": show,
            })
        })
        removeDataSourceByName(viewer, "GS_Object")
        const dataSource = new Cesium.CzmlDataSource();
        viewer && viewer.dataSources.add(dataSource.load([
            {
                "id": "document",
                "name": "GS_Object",
                "version": "1.0"
            },
            ...packet
        ]))
        requestRenderViewer(viewer);
    } catch (error) {
        console.log(error);
    }
}

// Function to take screenshot
const takeScreenshot = (viewer: Cesium.Viewer | null, setCoverageAnalysisImage: any) => {
    if (!viewer) return;

    viewer.render(); // Ensure latest frame is rendered
    const canvas = viewer.canvas;
    const image = canvas?.toDataURL("image/png");

    if (image) {
        setCoverageAnalysisImage(image)
    } else {
        console.error("Failed to get image data from canvas.");
    }
};

const zoomIntoTheEntity = (viewer: Cesium.Viewer | null, dataSourceName: string, entityId: string, setCoverageAnalysisImage?: any) => {
    // AT-10161 -> [if we zoom in case of 3D view then half globe will goes below the window]
    if (!viewer || !entityId || viewer?.scene?.mode === 3) return
    var entity = getEntityById(viewer, dataSourceName, entityId);
    if (entity) {
        viewer.flyTo(entity, {
            duration: 2,          // Duration of the flight in seconds
            offset: {             // Offset from the entity
                heading: Cesium.Math.toRadians(0),  // Camera heading in radians
                pitch: Cesium.Math.toRadians(-20),  // Camera pitch in radians
                range: 13000000       // Distance from entity in meters
            }
        }).then(() => {
            if (!setCoverageAnalysisImage) return
            takeScreenshot(viewer, setCoverageAnalysisImage)
        });
    } else {
        console.log('Entity not found');
    }
}

const stripMapping = (viewer: Cesium.Viewer | null, selectedAOI: AreaOfInterest, selectedSatellites: string[], setCoverageAnalysisImage: any) => {
    let coverageAnalysisReport = selectedAOI?.satelliteCoverage
    if (!viewer || !coverageAnalysisReport?.length) return
    try {
        removeDataSourceByName(viewer, 'STRIP_MAPPING')
        if (coverageAnalysisReport?.length > 0) {
            let coverageAreaBody: any = [] // list of coverage strip

            coverageAnalysisReport?.forEach((data) => {
                if (data?.visualisation_data && selectedSatellites.includes(data?.satellite_id)) {
                    data?.visualisation_data.map((visualization, index) => {
                        const cartographicGegreeData: number[] = [] // single strip object data
                        visualization?.geometry?.coordinates[0]?.map((coordinate: number[]) => {
                            cartographicGegreeData.push(coordinate[0], coordinate[1], 0) // pushing strip point into list
                        })
                        let coverage = {
                            "description": "area of interest - " + index,
                            "id": uuidv4(),
                            "name": "strip" + index,
                            "polygon": {
                                "extrudedHeight": 0,
                                "height": 0,
                                "material": {  // Color setting of the strip
                                    "solidColor": {
                                        "color": {
                                            "rgba": [0, 240, 0, 100]
                                        }
                                    }
                                },
                                "positions": {
                                    "cartographicDegrees": cartographicGegreeData // strip coordinated in the form of cartographic form
                                }
                            }
                        }
                        coverageAreaBody.push(coverage)
                    })
                }
            })

            if (coverageAreaBody?.length > 0) {
                viewer?.dataSources?.add(Cesium.CzmlDataSource.load([{
                    "id": "document",
                    "name": "STRIP_MAPPING",
                    "version": "1.0"
                }, ...coverageAreaBody]))
            }
            zoomIntoTheEntity(viewer, selectedAOI?.type === "Geofence" ? 'AOI_Object' : 'TARGET_Object', selectedAOI?.aoiId, setCoverageAnalysisImage)
            requestRenderViewer(viewer)
        }
    } catch (error) {
        console.log(error);
    }
}

const printSpotDataOnAoi = (viewer: Cesium.Viewer | null, selectedAOI: AreaOfInterest) => {
    removeDataSourceByName(viewer, "TARGET_OBJECT")
    if (!viewer || !selectedAOI?.allSpotData?.length) return
    try {
        const packet: any = []
        selectedAOI?.allSpotData.forEach((spot, index) => {
            const uniqueId = spot?.id;
            const latitude = spot?.lla.lat || 0;
            const longitude = spot?.lla.lng || 0;

            let targetColor = [spot?.revisit_color?.[0], spot?.revisit_color?.[1], spot?.revisit_color?.[2], 255]
            packet.push({
                "point": {
                    "color": {
                        "rgba": targetColor
                    },
                    "pixelSize": 3, // Size of the point in pixels
                    "show": true
                },
                "description": "Target Point",
                "position": {
                    "cartographicDegrees": [
                        longitude,
                        latitude,
                        0
                    ]
                },
                "id": uniqueId
            });
        });
        removeDataSourceByName(viewer, "TARGET_OBJECT")
        const dataSource = new Cesium.CzmlDataSource();
        viewer && viewer.dataSources.add(dataSource.load([
            {
                "id": "document",
                "name": "TARGET_OBJECT",
                "version": "1.0"
            },
            ...packet
        ]))
        requestRenderViewer(viewer);
    } catch (error) {
        console.log(error);
    }
}


//// ORBIT /////
const highlightGroup = (viewer: Cesium.Viewer | null, groupId: string, color?: string) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, groupId)
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (Cesium.defined(entity.label) && Cesium.defined(entity.billboard) && Cesium.defined(entity.path)) {
                    const colorString = color ? color : '[0, 245, 0, 255]';
                    const colorArray = getColourArray(colorString)
                    entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(colorArray[0], colorArray[1], colorArray[2], 255)); // orbit highlight color
                    entity.label.show = true;
                    entity.billboard.image = '/assets/satellite_default.svg';
                }
            }
            requestRenderViewer(viewer);
        }
    } catch (error) {
        console.log(error);
    }
}
const highlightGroupSatellite = (viewer: Cesium.Viewer | null, groupId: string, satelliteId: string) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, groupId)
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (entity.id === satelliteId && Cesium.defined(entity.label) && Cesium.defined(entity.billboard) && Cesium.defined(entity.path)) {
                    entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(0, 245, 0, 240)); // orbit highlight color
                    entity.label.show = true;
                    entity.billboard.image = '/assets/satellite_default.svg';
                }
            }
            requestRenderViewer(viewer);
        }
    } catch (error) {
        console.log(error);
    }
}
const obscureHighlightGroup = (viewer: Cesium.Viewer | null, groups: GroupOrbitDataType[]) => {
    if (!viewer) return
    try {
        groups.forEach(group => {
            const groupId = group.group_id
            const satelliteMap = new Map(group.orbit_report.map(sat => [sat.satelliteId, sat.orbitColor])); // Map for quick lookup
            const datasource: any = getDataSourceByName(viewer, groupId)
            if (datasource && datasource.entities && datasource.entities.values) {
                for (const entity of datasource.entities.values) {
                    if (satelliteMap.get(entity.id) && Cesium.defined(entity.label) && Cesium.defined(entity.billboard) && Cesium.defined(entity.path)) {
                        const colorString = satelliteMap.get(entity.id) as string;
                        const colorArray = getColourArray(colorString)
                        entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(colorArray[0], colorArray[1], colorArray[2], 255)); // orbit highlight color
                        entity.label.show = false;
                        entity.billboard.image = '/assets/satellite_inactive.svg';
                    }
                }
                requestRenderViewer(viewer);
            }
        })
    } catch (error) {
        console.log(error);
    }
}

//// GROUND STATIONS ////
const hoverGroundStation = (viewer: Cesium.Viewer | null, stationId: string | null) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, 'GS_Object')
        if (datasource?.entities?.values) {
            for (const entity of datasource.entities.values) {
                if (entity?.billboard && Cesium.defined(entity.billboard) && entity.id === stationId) {
                    entity.billboard.image = '/assets/gs_hover.svg'
                }
            }
            requestRenderViewer(viewer);
        }
    } catch (error) {
        console.log(error);
    }
}

const highlightGroundStations = (viewer: Cesium.Viewer | null, stations: string[]) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, "GS_Object")
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (entity && entity.billboard && Cesium.defined(entity.billboard)) {
                    if (stations.includes(entity.id)) {
                        entity.billboard.image = '/assets/select_gs.svg'
                    } else {
                        entity.billboard.image = '/assets/un_select_gs.svg';
                    }
                }
            }
            requestRenderViewer(viewer);
        }
    } catch (error) {
        console.log(error);
    }
}
const addOrUpdateGroundStation = (viewer: Cesium.Viewer | null, station: {
    name: string;
    latitude?: string;
    longitude?: string;
    uniqueId: string;
}) => {
    if (!viewer) return;
    try {
        const entityId = station.uniqueId;
        const dataSource = getDataSourceByName(viewer, "GS_Object");

        if (!dataSource) return
        const existingEntity = getEntityById(viewer, 'GS_Object', entityId);

        if (existingEntity && existingEntity.label && existingEntity.position && Cesium.defined(existingEntity.position)) {
            existingEntity.label.text = new Cesium.ConstantProperty(station.name);
            if (station.longitude && station.latitude) {
                existingEntity.position = new Cesium.ConstantPositionProperty(
                    Cesium.Cartesian3.fromDegrees(
                        Number(station.longitude) || 0,
                        Number(station.latitude) || 0,
                        0
                    )
                );
            }
        } else {
            dataSource.entities.add({
                id: entityId,
                name: station.name,
                position: Cesium.Cartesian3.fromDegrees(
                    Number(station.longitude) || 0,
                    Number(station.latitude) || 0,
                    0
                ),
                billboard: {
                    image: '/assets/select_gs.svg',
                    scale: 0.8,
                    verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                },
                label: {
                    text: station.name,
                    font: 'montserrat',
                    horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                    pixelOffset: new Cesium.Cartesian2(15, 0),
                    show: false,
                }
            });
        }
        requestRenderViewer(viewer);

    } catch (error) {
        console.log(error);
    }
};


//// GEO FEATURES ////
const highlightGroundTargets = (viewer: Cesium.Viewer | null, targets: string[], selected?: boolean) => {
    if (!viewer) return
    try {
        const datasource: any = getDataSourceByName(viewer, "TARGET_Object")
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (entity && entity.billboard && Cesium.defined(entity.billboard)) {
                    if (targets.includes(entity.id) && selected) {
                        entity.billboard.image = '/assets/point_green.png'
                    } else if (targets.includes(entity.id)) {
                        entity.billboard.image = '/assets/point_white_hover.png'
                    } else {
                        entity.billboard.image = '/assets/point_white.png';
                    }
                }
            }
            requestRenderViewer(viewer);
        }
    } catch (error) {
        console.log(error);
    }
}

const applyAoiVisualStyle = (entity: any, fillColor: Cesium.Color, outlineColor: string) => {
    entity.polygon.material = new Cesium.ColorMaterialProperty(fillColor);
    entity.polygon.material.definitionChanged.raiseEvent(entity.polygon.material);
    entity.polygon.outline = true;

    if (entity.polyline && Cesium.defined(entity.polyline)) {
        entity.polyline.width = new Cesium.ConstantProperty(2);
        entity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(outlineColor));
    }
}

const highlightAois = (viewer: Cesium.Viewer | null, aois: string[], selected?: boolean) => {
    if (!viewer) return;
    try {
        const datasource: any = getDataSourceByName(viewer, "AOI_Object");
        if (datasource && datasource.entities && datasource.entities.values) {
            for (const entity of datasource.entities.values) {
                if (entity && entity.polygon && Cesium.defined(entity.polygon)) {
                    if (aois.includes(entity.id) && selected) {
                        applyAoiVisualStyle(entity, offline() ? Cesium.Color.fromBytes(0, 255, 0, 100) : Cesium.Color.fromBytes(204, 245, 78, 20), "#00ff00");
                    } else if (aois.includes(entity.id)) {
                        applyAoiVisualStyle(entity, Cesium.Color.fromBytes(9, 9, 9, 50), "#ffffff");
                    } else {
                        applyAoiVisualStyle(entity, Cesium.Color.fromBytes(240, 240, 240, 10), "#ffffff");
                    }
                }
                requestRenderViewer(viewer);
            }
        }
    } catch (error) {
        console.log(error);
    }
};

const showDefaultVisualizationData = (viewer: Cesium.Viewer | null, hiddenEntities: string[], resetView: boolean = false) => {
    if (!viewer) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        if (entity && entity.id && hiddenEntities.includes(entity.id)) {
            entity.show = false;
        } else {
            entity.show = true;
        }
    }

    removeDataSourceByName(viewer, 'STRIP_MAPPING')
    removeDataSourceByName(viewer, 'TARGET_OBJECT')
    requestRenderViewer(viewer);

    if (resetView) {
        setTimeout(() => {
            viewer.scene.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(0, 0, 35500000),
            });
        }, 100)
    }
}

const toggleHideShowEntity = (viewer: Cesium.Viewer | null, entityId: string) => {
    if (!viewer) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        if (entity && entity.id === entityId) {
            entity.show = !entity.show;
        }
    }
    requestRenderViewer(viewer);
}

//////////////////// REPORT VISUALIZATION //////////////////////
const visualizeCoverageReportData = (viewer: Cesium.Viewer | null, selectedAOI: AreaOfInterest, selectedSatellites: string[], setCoverageAnalysisImage: any) => {
    if (!viewer) return

    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        const description = (entity && entity.description) && entity.description.getValue(Cesium.JulianDate.now());
        if (entity && (description === 'target' || description === 'aoi') && (entity.id === selectedAOI.aoiId)) {
            entity.show = true;
        } else if (entity && entity.path && description === 'orbit' && selectedSatellites.includes(entity.id) && Cesium.defined(entity.path)) {
            // entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(255, 255, 255, 255)); // orbit highlight color
            entity.show = true;
        } else if (entity && description) {
            entity.show = false;
        }
    }
    stripMapping(viewer, selectedAOI, selectedSatellites, setCoverageAnalysisImage);
    printSpotDataOnAoi(viewer, selectedAOI)
    requestRenderViewer(viewer);
}

const visualizePropultionReportData = (viewer: Cesium.Viewer | null, selectedSatellites: string[]) => {
    if (!viewer) return

    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        const description = (entity && entity.description) && entity.description.getValue(Cesium.JulianDate.now());
        if (entity && entity.path && description === 'orbit' && selectedSatellites.includes(entity.id) && Cesium.defined(entity.path)) {
            // entity.path.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromBytes(255, 255, 255, 255)); // orbit highlight color
            entity.show = true;
        } else if (entity && description) {
            entity.show = false;
        }
    }
    requestRenderViewer(viewer);
}

const visualizeContactReportData = (viewer: Cesium.Viewer | null, satellites: string[], stations: string[]) => {
    if (!viewer) return
    const reportEntities: string[] = [...satellites, ...stations];
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        const description = (entity && entity.description) && entity.description.getValue(Cesium.JulianDate.now());
        if (entity && description && reportEntities.includes(entity.id)) {
            entity.show = true;
        } else if (entity && description) {
            entity.show = false;
        }
    }
    if (stations.length === 1) {
        zoomIntoTheEntity(viewer, 'GS_Object', stations[0]);
    } else {
        setTimeout(() => {
            viewer.scene.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(0, 0, 35500000),
            });
        }, 100)
    }
    requestRenderViewer(viewer);
}

const visualizeGroundLinkReportData = (viewer: Cesium.Viewer | null, reportEntities: string[]) => {
    if (!viewer) return
    const entities = getAllEntitiesFromDataSources(viewer);
    if (!entities) return
    for (const entity of entities) {
        const description = (entity && entity.description) && entity.description.getValue(Cesium.JulianDate.now());
        if (entity && description && reportEntities.includes(entity.id)) {
            entity.show = true;
        } else if (entity && description) {
            entity.show = false;
        }
    }
    requestRenderViewer(viewer);
}

export const Visualization = {
    getAllEntitiesFromDataSources,
    highlightAois,
    highlightGroup,
    removeEntityById,
    createOrbitsPackets,
    toggleHideShowEntity,
    obscureHighlightGroup,
    highlightGroundTargets,
    removeDataSourceByName,
    highlightGroupSatellite,
    highlightGroundStations,
    hoverGroundStation,
    addOrUpdateGroundStation,
    createAllGeoFeaturePacket,
    createAllGroundStationsPacket,
    showDefaultVisualizationData,
    visualizeCoverageReportData,
    visualizePropultionReportData,
    visualizeContactReportData,
    visualizeGroundLinkReportData,
    requestRenderViewer,
    takeScreenshot,
    hideEntity,
    showEntity
}